import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import { Card, Badge, Button, Spinner } from "../components/common/UIKit";
import { listSubscriptions, listMyCounties, getAccess } from "../api/subscriptions";
import { useAuthStore } from "../store/authStore";
import { ArrowUpRight, Map, CreditCard, ShieldCheck, Layers } from "lucide-react";

export default function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const [subs, setSubs] = useState([]);
  const [counties, setCounties] = useState([]);
  const [access, setAccess] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([listSubscriptions(), listMyCounties(), getAccess()])
      .then(([s, c, a]) => {
        setSubs(s);
        setCounties(c);
        setAccess(a);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const activeSubs = subs.filter((s) => s.status === "active");
  const allTypesCount = activeSubs.filter((s) => s.is_all_types).length;
  const perTypeCount = activeSubs.filter((s) => !s.is_all_types).length;

  const stats = [
    { label: "Counties", value: counties.length, icon: Map, testId: "stat-counties" },
    { label: "Active subs", value: activeSubs.length, icon: ShieldCheck, testId: "stat-active-subs" },
    { label: "All-type plans", value: allTypesCount, icon: Layers, testId: "stat-all-type" },
    { label: "Per-type plans", value: perTypeCount, icon: CreditCard, testId: "stat-per-type" },
  ];

  return (
    <AppShell
      title={`Welcome, ${user?.full_name?.split(" ")[0] || "Agent"}`}
      subtitle="Your lead access at a glance."
      actions={
        <Link to="/leads">
          <Button variant="accent" data-testid="dashboard-open-leads">
            Open Leads Browser <ArrowUpRight className="h-4 w-4 ml-1" />
          </Button>
        </Link>
      }
    >
      {loading ? (
        <div className="min-h-[calc(100vh-180px)] flex flex-col items-center justify-center gap-3 px-8" data-testid="dashboard-loading">
          <Spinner />
          <p className="text-sm font-semibold text-zinc-950">Loading your dashboard...</p>
          <p className="text-xs text-zinc-500">Fetching subscriptions, counties, and access</p>
        </div>
      ) : (
        <div className="p-8 space-y-6" data-testid="dashboard-content">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map(({ label, value, icon: Icon, testId }) => (
              <Card key={label} data-testid={testId}>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase font-bold tracking-widest text-zinc-500">{label}</p>
                  <Icon className="h-4 w-4 text-zinc-400" />
                </div>
                <p className="text-4xl font-black tracking-tight text-zinc-950 mt-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {value}
                </p>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold tracking-tight text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Active subscriptions
                </h2>
                <Link to="/subscriptions" className="text-xs font-semibold text-zinc-600 hover:text-zinc-950">
                  Manage →
                </Link>
              </div>
              {activeSubs.length === 0 ? (
                <p className="text-sm text-zinc-500 py-6 text-center" data-testid="no-active-subs">
                  No active subscriptions yet. Pick a county to get started.
                </p>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {activeSubs.slice(0, 8).map((s) => (
                    <li key={s.id} className="py-3 flex items-center justify-between" data-testid={`active-sub-${s.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-zinc-950">
                          {s.state} / {s.county_name}
                        </p>
                        <p className="text-xs text-zinc-500">
                          {s.is_all_types ? "All lead types" : s.lead_type_name?.toUpperCase()}
                        </p>
                      </div>
                      <Badge color={s.is_all_types ? "blue" : "green"}>{s.is_all_types ? "ALL" : "1 TYPE"}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold tracking-tight text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Your access map
                </h2>
              </div>
              {access.length === 0 ? (
                <p className="text-sm text-zinc-500 py-6 text-center">
                  No lead access yet. Subscribe to unlock counties.
                </p>
              ) : (
                <ul className="space-y-2">
                  {access.map((a, i) => (
                    <li key={i} className="flex items-center justify-between border-b border-zinc-100 pb-2">
                      <p className="text-sm font-semibold text-zinc-950">
                        {a.state} / {a.county_name}
                      </p>
                      <p className="text-xs text-zinc-500">
                        {a.is_all_types ? "All 9 types" : `${a.lead_types.length} type${a.lead_types.length !== 1 ? "s" : ""}`}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </div>
      )}
    </AppShell>
  );
}
